import fetchItemPolicyEvaluations from 'actions/businessTraveller/fetchItemPolicyEvaluations'
import useTripRequestId from 'businessTraveller/hooks/useTripRequestId'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect } from 'react'

interface Params {
  enabled: boolean
  businessId?: string
  cacheKey: string
  item: App.BusinessTraveller.ItemPolicyEvaluationItem | undefined
  employeeIds: Array<string>
}

/**
 * Shared item-policy evaluation primitive: dispatches one evaluate request for `item` under `cacheKey`
 * and returns the stored result. The budget bar (cost-less items) and the offer credit badge (cost
 * items) both build on this — they differ only in the item they pass and how they read the result.
 */
function useItemPolicyEvaluation({
  enabled,
  businessId,
  cacheKey,
  item,
  employeeIds,
}: Params): App.BusinessTraveller.StatefulData<App.BusinessTraveller.ItemPolicyEvaluationResult> | undefined {
  const appDispatch = useAppDispatch()
  const tripRequestId = useTripRequestId()
  const evaluation = useAppSelector(state => state.businessTraveller.itemPolicyEvaluations?.[cacheKey])
  const employeeIdsKey = employeeIds.join(',')

  useEffect(() => {
    if (!enabled || !businessId || !cacheKey || !item || employeeIds.length === 0) {
      return
    }
    appDispatch(fetchItemPolicyEvaluations([cacheKey, item, employeeIds, tripRequestId], businessId))
    // cacheKey encodes semantic request identity; callers regenerate it when any input changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-firing on new `item` object identity each render
  }, [appDispatch, enabled, businessId, cacheKey, employeeIdsKey, tripRequestId])

  return evaluation
}

export default useItemPolicyEvaluation
